import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, AI_TIERS, type AiModelsPolicy, type AiTier, type TenantBase } from '../lib/api';
import { TIER_LABELS } from '../lib/format';
import { Button, Card, ErrorText, Modal, Switch } from './ui';
import { AiBillingCard } from './AiBillingCard';
import { Select } from './Select';
import { AppliedHint } from './AppliedHint';

const TIER_HINTS: Record<AiTier, string> = {
  haiku: 'rapid și ieftin: întrebări scurte, rezumate',
  sonnet: 'echilibrat: majoritatea cererilor, analize, documente',
  opus: 'cel mai capabil și cel mai scump: analize în profunzime, minute de întâlnire',
};

function samePolicy(a: AiModelsPolicy | null, b: AiModelsPolicy | null): boolean {
  if (a === null || b === null) return a === b;
  return a.default === b.default && a.allowed.join() === b.allowed.join();
}

/**
 * Which AI models the tenant bought and which one its chat opens on. "Toate
 * modelele" means no policy (Haiku, Sonnet and Opus, the instance's own
 * default). The instance applies the policy at its next heartbeat, to EVERY
 * AI call it makes: chat, MCP, meeting minutes, summaries, admins included.
 */
function ModelsCard({ tenant, lastSeenAt }: { tenant: TenantBase; lastSeenAt: string | null }) {
  const queryClient = useQueryClient();
  const saved = tenant.aiModels;
  const [all, setAll] = useState(saved === null);
  const [allowed, setAllowed] = useState<Set<AiTier>>(() => new Set(saved?.allowed ?? AI_TIERS));
  const [def, setDef] = useState<'auto' | AiTier>(saved?.default ?? 'auto');
  const [confirming, setConfirming] = useState(false);

  const draft = useMemo<AiModelsPolicy | null>(() => {
    if (all) return null;
    const tiers = AI_TIERS.filter((t) => allowed.has(t));
    return { allowed: tiers, default: def !== 'auto' && !tiers.includes(def) ? 'auto' : def };
  }, [all, allowed, def]);

  const dirty = !samePolicy(draft, saved);
  const nothingAllowed = draft !== null && draft.allowed.length === 0;

  const mutation = useMutation({
    mutationFn: () => api.setTenantAiModels(tenant.id, draft),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries();
    },
  });

  function toggle(tier: AiTier, on: boolean) {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (on) next.add(tier);
      else next.delete(tier);
      return next;
    });
  }

  function reset() {
    setAll(saved === null);
    setAllowed(new Set(saved?.allowed ?? AI_TIERS));
    setDef(saved?.default ?? 'auto');
  }

  const defaultOptions: Array<'auto' | AiTier> = [
    'auto',
    ...AI_TIERS.filter((t) => all || allowed.has(t)),
  ];

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-white">Modele AI disponibile</h2>
        <p className="mt-1 text-xs text-slate-400">
          Se aplică la tot ce rulează pe Firmio AI în instanță: chat, MCP, minute de întâlnire,
          rezumate. Un client fără Opus nu plătește Opus nicăieri, nici ca admin.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 px-3 py-2.5">
        <div>
          <div className="text-sm text-white">Toate modelele</div>
          <div className="text-xs text-slate-400">
            Fără restricții: Haiku, Sonnet și Opus, cu modelul implicit al instanței.
          </div>
        </div>
        <Switch checked={all} onChange={setAll} label="Toate modelele" />
      </div>

      <ul className="divide-y divide-slate-800/70 rounded-lg border border-slate-800">
        {AI_TIERS.map((tier) => (
          <li key={tier} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <div
                className={`text-sm ${all || allowed.has(tier) ? 'text-white' : 'text-slate-400'}`}
              >
                {TIER_LABELS[tier]} 5.5
              </div>
              <div className="text-xs text-slate-500">{TIER_HINTS[tier]}</div>
            </div>
            <Switch
              checked={all || allowed.has(tier)}
              disabled={all}
              onChange={(next) => toggle(tier, next)}
              label={TIER_LABELS[tier]}
            />
          </li>
        ))}
      </ul>

      <div>
        <span className="text-xs uppercase tracking-wide text-slate-400">Model implicit</span>
        <Select
          label="Model implicit"
          value={def}
          onChange={(v) => setDef(v as 'auto' | AiTier)}
          disabled={all}
          options={defaultOptions.map((o) => ({
            value: o,
            label: o === 'auto' ? 'Auto' : `${TIER_LABELS[o]} 5.5`,
            description: o === 'auto' ? 'instanța alege modelul pe fiecare mesaj' : TIER_HINTS[o],
          }))}
          className="mt-1"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Cu ce model se deschide chat-ul pentru cine n-a ales unul. Utilizatorii pot alege alt
          model dintre cele permise.
        </span>
      </div>

      {nothingAllowed && <p className="text-sm text-red-300">Lasă cel puțin un model pornit.</p>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <AppliedHint savedAt={tenant.updatedAt} lastSeenAt={lastSeenAt} />
        <div className="flex gap-2">
          <Button variant="ghost" disabled={!dirty} onClick={reset}>
            Renunță
          </Button>
          <Button disabled={!dirty || nothingAllowed} onClick={() => setConfirming(true)}>
            Salvează
          </Button>
        </div>
      </div>

      {confirming && (
        <Modal
          title={`Salvezi modelele AI pentru ${tenant.name}?`}
          onClose={() => setConfirming(false)}
        >
          <div className="space-y-4">
            {draft === null ? (
              <p className="text-sm text-slate-300">
                Clientul primește toate modelele, cu modelul implicit al instanței.
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-300">
                  La următorul heartbeat, toate apelurile AI ale instanței, inclusiv minutele de
                  întâlnire și cele ale adminilor, rulează doar pe{' '}
                  {draft.allowed.map((t) => TIER_LABELS[t]).join(', ')}. Un model mai mare cerut de
                  cineva se înlocuiește cu cel mai apropiat permis.
                </p>
                <p className="text-sm text-slate-300">
                  Chat-ul se deschide pe{' '}
                  {draft.default === 'auto' ? 'Auto' : `${TIER_LABELS[draft.default]} 5.5`}.
                </p>
              </>
            )}
            <ErrorText error={mutation.error} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Renunță
              </Button>
              <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                Salvează
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

export function AiTab({
  tenant,
  lastSeenAt,
  aiTotalEur,
}: {
  tenant: TenantBase;
  lastSeenAt: string | null;
  aiTotalEur: number | null;
}) {
  const ownKeys = tenant.aiBilling.source === 'own';
  return (
    <div className="space-y-4">
      <AiBillingCard tenant={tenant} lastSeenAt={lastSeenAt} aiTotalEur={aiTotalEur} />
      {ownKeys ? (
        <Card>
          <h2 className="text-sm font-semibold text-white">Modele AI disponibile</h2>
          <p className="mt-1 text-sm text-slate-400">
            Clientul folosește cheile lui, deci își alege singur modelele; consola nu le plafonează.
            Selecția de modele e parte din abonamentul Firmio.
          </p>
        </Card>
      ) : (
        <ModelsCard tenant={tenant} lastSeenAt={lastSeenAt} />
      )}
    </div>
  );
}
